import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Avatar,
  alpha,
} from '@mui/material';
import { VerifiedUser, Security, ArrowForward } from '@mui/icons-material';
import BaseTab from './BaseTab';

interface FrontendSecurityTabProps {
  isDark: boolean;
  colors: any;
  discordFont: any;
  glassStyle: any;
  lokiGradient: string;
  goldColor: string;
  isMobile: boolean;
}

const FrontendSecurityTab: React.FC<FrontendSecurityTabProps> = (props) => {
  return (
    <BaseTab
      {...props}
      title="Frontend Security Features"
      emoji="🔒"
    >
      <Typography variant="h6" sx={{ color: props.goldColor, mb: 3 }}>
        Comprehensive Security Implementation
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderLeft: `4px solid ${props.goldColor}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: props.goldColor }}>
                Frontend Security
              </Typography>
              <List dense>
                {[
                  'XSS Protection - Sanitized user inputs',
                  'CSRF Protection - Token-based validation',
                  'Content Security Policy - Strict CSP headers',
                  'Secure Storage - Encrypted localStorage',
                  'Input Validation - Comprehensive form validation',
                ].map((item, idx) => (
                  <ListItem key={idx} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <VerifiedUser sx={{ color: props.goldColor }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={item} 
                      primaryTypographyProps={{ color: props.colors.textPrimary }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderLeft: `4px solid #0F3B2E` }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: '#0F3B2E' }}>
                Authentication Security
              </Typography>
              <List dense>
                {[
                  'OAuth 2.0 - Secure third-party auth',
                  'JWT Tokens - Stateless session management',
                  'Password Policies - Strong requirements',
                  'Rate Limiting - API request throttling',
                  'Session Timeout - Auto logout',
                ].map((item, idx) => (
                  <ListItem key={idx} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Security sx={{ color: '#0F3B2E' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={item} 
                      primaryTypographyProps={{ color: props.colors.textPrimary }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 2, color: props.goldColor }}>
        Authentication Flow
      </Typography>
      <Paper sx={{ p: 3, bgcolor: alpha('#051814', 0.8), borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          {[
            { step: 1, title: 'Google OAuth', desc: 'Redirect to Google' },
            { step: 2, title: 'JWT Tokens', desc: 'Store in localStorage' },
            { step: 3, title: 'Auto-refresh', desc: 'Token renewal' },
            { step: 4, title: 'Protected Routes', desc: 'Route guards' },
          ].map((flow, idx) => (
            <React.Fragment key={idx}>
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Avatar sx={{ bgcolor: props.goldColor, mb: 1, mx: 'auto' }}>
                  {flow.step}
                </Avatar>
                <Typography variant="subtitle2" fontWeight={700} color={props.colors.textPrimary}>
                  {flow.title}
                </Typography>
                <Typography variant="caption" color={props.colors.textSecondary}>
                  {flow.desc}
                </Typography>
              </Box>
              {idx < 3 && (
                <ArrowForward sx={{ color: props.goldColor, display: { xs: 'none', md: 'block' } }} />
              )}
            </React.Fragment>
          ))}
        </Stack>
      </Paper>
    </BaseTab>
  );
};

export default FrontendSecurityTab;